import * as THREE from 'three';
import type { Projector } from './projector';

type PixelSizeUserData = {
  projectorOverlayIgnore?: boolean;
  projectorPixelSizeState?: {
    transparent: boolean;
    depthWrite: boolean;
    depthTest: boolean;
    blending: THREE.Blending;
    premultipliedAlpha: boolean;
  };
  projectorFrustumPixelSize?: {
    applied?: boolean;
    uniforms?: THREE.Shader['uniforms'];
  };
};

export type ProjectorFrustumPixelSizeOptions = {
  maxProjectors?: number;
  warningColor?: THREE.ColorRepresentation;
  warningOpacity?: number;
  idealPixelSize?: number; // in meters (e.g., 0.002 for 2mm)
  facingThreshold?: number;
};

export class ProjectorFrustumPixelSize {
  private readonly maxProjectors: number;
  private readonly warningColor: THREE.Color;
  private warningOpacity: number;
  private idealPixelSize: number;
  private readonly materials = new Set<THREE.Material>();
  private readonly matrices: THREE.Matrix4[];
  private readonly projectorPositions: THREE.Vector3[];
  private readonly projectorDirections: THREE.Vector3[];
  private readonly projectorResolutions: THREE.Vector2[];
  private readonly projectorTanHalfFov: THREE.Vector2[]; // tan(fovX/2), tan(fovY/2)
  private facingThreshold: number;
  private overlayOnly = false;

  constructor(options: ProjectorFrustumPixelSizeOptions = {}) {
    this.maxProjectors = Math.max(1, options.maxProjectors ?? 16);
    this.warningColor = new THREE.Color(options.warningColor ?? 0xff6b35); // Orange warning color
    this.warningOpacity = options.warningOpacity ?? 0.6;
    this.idealPixelSize = options.idealPixelSize ?? 0.002; // 2mm default
    this.facingThreshold = options.facingThreshold ?? -1;
    this.matrices = Array.from({ length: this.maxProjectors }, () => new THREE.Matrix4());
    this.projectorPositions = Array.from({ length: this.maxProjectors }, () => new THREE.Vector3());
    this.projectorDirections = Array.from({ length: this.maxProjectors }, () => new THREE.Vector3(0, 0, -1));
    this.projectorResolutions = Array.from({ length: this.maxProjectors }, () => new THREE.Vector2(1920, 1080));
    this.projectorTanHalfFov = Array.from({ length: this.maxProjectors }, () => new THREE.Vector2(1, 1));
  }

  applyTo(root: THREE.Object3D) {
    root.traverse((object) => {
      if (!('isMesh' in object) || !(object as THREE.Mesh).isMesh) {
        return;
      }

      const mesh = object as THREE.Mesh;
      if ((mesh.userData as PixelSizeUserData).projectorOverlayIgnore) {
        return;
      }

      if (Array.isArray(mesh.material)) {
        mesh.material.forEach((material) => this.decorateMaterial(material));
      } else if (mesh.material) {
        this.decorateMaterial(mesh.material);
      }
    });
  }

  update(projectors: Projector[], options: { overlayOnly?: boolean } = {}) {
    const count = Math.min(projectors.length, this.maxProjectors);
    this.overlayOnly = options.overlayOnly ?? false;
    if (count === 0) {
      this.applyUniforms(0);
      return;
    }

    for (let i = 0; i < count; i += 1) {
      const projector = projectors[i];
      projector.updateMatrixWorld(true);
      projector.camera.updateMatrixWorld(true);
      this.matrices[i].multiplyMatrices(projector.camera.projectionMatrix, projector.camera.matrixWorldInverse);
      this.projectorPositions[i].setFromMatrixPosition(projector.camera.matrixWorld);
      projector.camera.getWorldDirection(this.projectorDirections[i]);

      // Store resolution
      this.projectorResolutions[i].copy(projector.userData?.resolution);

      // Calculate tan(fov/2) for both axes
      const fovY = THREE.MathUtils.degToRad(projector.camera.fov);
      const tanHalfFovY = Math.tan(fovY / 2);
      const tanHalfFovX = tanHalfFovY * projector.camera.aspect;
      this.projectorTanHalfFov[i].set(tanHalfFovX, tanHalfFovY);
    }

    this.applyUniforms(count);
  }

  setWarningOpacity(opacity: number) {
    this.warningOpacity = Math.min(1, Math.max(0, opacity));
  }

  setWarningColor(color: THREE.ColorRepresentation) {
    this.warningColor.set(color);
  }

  setIdealPixelSize(size: number) {
    this.idealPixelSize = Math.max(0.0001, size);
  }

  getIdealPixelSize() {
    return this.idealPixelSize;
  }

  setFacingThreshold(threshold: number) {
    this.facingThreshold = Math.max(-1, Math.min(1, threshold));
  }

  clearOverlay() {
    this.overlayOnly = false;
    this.applyUniforms(0);
  }

  getBatchSize() {
    return this.maxProjectors;
  }

  beginOverlayPass() {
    for (const material of this.materials) {
      const userData = material.userData as PixelSizeUserData;
      if (!userData.projectorPixelSizeState) {
        userData.projectorPixelSizeState = {
          transparent: material.transparent,
          depthWrite: material.depthWrite,
          depthTest: material.depthTest,
          blending: material.blending,
          premultipliedAlpha: material.premultipliedAlpha,
        };
      }

      material.transparent = false;
      material.depthWrite = true;
      material.depthTest = true;
      material.blending = THREE.NormalBlending;
      material.premultipliedAlpha = false;
    }
  }

  endOverlayPass() {
    for (const material of this.materials) {
      const userData = material.userData as PixelSizeUserData;
      const state = userData.projectorPixelSizeState;
      if (!state) {
        continue;
      }

      material.transparent = state.transparent;
      material.depthWrite = state.depthWrite;
      material.depthTest = state.depthTest;
      material.blending = state.blending;
      material.premultipliedAlpha = state.premultipliedAlpha;
    }
  }

  private decorateMaterial(material: THREE.Material) {
    if (!('isMeshStandardMaterial' in material) && !('isMeshPhysicalMaterial' in material)) {
      return;
    }

    const userData = material.userData as PixelSizeUserData;
    if (userData.projectorFrustumPixelSize?.applied) {
      return;
    }

    const previous = material.onBeforeCompile;
    material.onBeforeCompile = (shader) => {
      previous?.(shader);

      shader.uniforms.uPixelSizeProjectorCount = { value: 0 };
      shader.uniforms.uPixelSizeProjectorMatrix = {
        value: this.matrices.map((m) => m.clone()),
      };
      shader.uniforms.uPixelSizeProjectorPosition = {
        value: this.projectorPositions.map((p) => p.clone()),
      };
      shader.uniforms.uPixelSizeProjectorDirection = {
        value: this.projectorDirections.map((d) => d.clone()),
      };
      shader.uniforms.uPixelSizeProjectorResolution = {
        value: this.projectorResolutions.map((r) => r.clone()),
      };
      shader.uniforms.uPixelSizeProjectorTanHalfFov = {
        value: this.projectorTanHalfFov.map((t) => t.clone()),
      };
      shader.uniforms.uPixelSizeWarningColor = { value: this.warningColor };
      shader.uniforms.uPixelSizeWarningOpacity = { value: this.warningOpacity };
      shader.uniforms.uPixelSizeIdealSize = { value: this.idealPixelSize };
      shader.uniforms.uPixelSizeOverlayOnly = { value: 0 };
      shader.uniforms.uPixelSizeFacingThreshold = {
        value: this.facingThreshold,
      };

      shader.vertexShader = shader.vertexShader
        .replace(
          '#include <common>',
          '#include <common>\n' + 'varying vec3 vPixelSizeWorldPosition;\n' + 'varying vec3 vPixelSizeWorldNormal;'
        )
        .replace(
          '#include <project_vertex>',
          [
            '#include <project_vertex>',
            'vec4 pixelSizeWorldPosition = vec4( transformed, 1.0 );',
            '#ifdef USE_BATCHING',
            '  pixelSizeWorldPosition = batchingMatrix * pixelSizeWorldPosition;',
            '#endif',
            '#ifdef USE_INSTANCING',
            '  pixelSizeWorldPosition = instanceMatrix * pixelSizeWorldPosition;',
            '#endif',
            'pixelSizeWorldPosition = modelMatrix * pixelSizeWorldPosition;',
            'vPixelSizeWorldPosition = pixelSizeWorldPosition.xyz;',
            'vPixelSizeWorldNormal = normalize((modelMatrix * vec4(objectNormal, 0.0)).xyz);',
          ].join('\n')
        );

      const fragmentHeaderLines = [
        '#include <common>',
        `#define EDGE_MAX_PROJECTORS ${this.maxProjectors}`,
        'varying vec3 vPixelSizeWorldPosition;',
        'varying vec3 vPixelSizeWorldNormal;',
        'uniform int uPixelSizeProjectorCount;',
        'uniform mat4 uPixelSizeProjectorMatrix[EDGE_MAX_PROJECTORS];',
        'uniform vec3 uPixelSizeProjectorPosition[EDGE_MAX_PROJECTORS];',
        'uniform vec3 uPixelSizeProjectorDirection[EDGE_MAX_PROJECTORS];',
        'uniform vec2 uPixelSizeProjectorResolution[EDGE_MAX_PROJECTORS];',
        'uniform vec2 uPixelSizeProjectorTanHalfFov[EDGE_MAX_PROJECTORS];',
        'uniform vec3 uPixelSizeWarningColor;',
        'uniform float uPixelSizeWarningOpacity;',
        'uniform float uPixelSizeIdealSize;',
        'uniform float uPixelSizeOverlayOnly;',
        'uniform float uPixelSizeFacingThreshold;',
      ];

      const fragmentHeader = fragmentHeaderLines.join('\n');

      // Simplified shader without depth testing - uses frustum bounds and facing check
      const pixelSizeBlocks = [
        'float pixelSizeMaxRatio = 0.0;',
        'vec3 pixelSizeViewPos = -vViewPosition;',
        'for (int i = 0; i < EDGE_MAX_PROJECTORS; i++) {',
        '  if (i >= uPixelSizeProjectorCount) { break; }',
        '  vec4 clip = uPixelSizeProjectorMatrix[i] * vec4(vPixelSizeWorldPosition, 1.0);',
        '  if (clip.w <= 0.0) { continue; }',
        '  vec3 ndc = clip.xyz / clip.w;',
        '  // Check if point is within frustum',
        '  if (ndc.x < -1.0 || ndc.x > 1.0 || ndc.y < -1.0 || ndc.y > 1.0 || ndc.z < -1.0 || ndc.z > 1.0) { continue; }',
        '  // Facing check',
        '  if (uPixelSizeFacingThreshold > -0.999) {',
        '    vec3 toProjector = normalize(uPixelSizeProjectorPosition[i] - vPixelSizeWorldPosition);',
        '    if (dot(vPixelSizeWorldNormal, toProjector) <= uPixelSizeFacingThreshold) { continue; }',
        '  }',
        '',
        '  // Calculate distance along projection axis',
        '  vec3 toFragment = vPixelSizeWorldPosition - uPixelSizeProjectorPosition[i];',
        '  float distanceToProjector = max(dot(toFragment, uPixelSizeProjectorDirection[i]), 0.001);',
        '',
        '  // Calculate physical size of projection at this distance',
        '  float physicalWidth = 2.0 * distanceToProjector * uPixelSizeProjectorTanHalfFov[i].x;',
        '  float physicalHeight = 2.0 * distanceToProjector * uPixelSizeProjectorTanHalfFov[i].y;',
        '',
        '  // Base pixel size (without angle consideration)',
        '  float basePixelWidth = physicalWidth / uPixelSizeProjectorResolution[i].x;',
        '  float basePixelHeight = physicalHeight / uPixelSizeProjectorResolution[i].y;',
        '',
        '  // Calculate stretch factor due to surface angle',
        '  vec3 projDir = uPixelSizeProjectorDirection[i];',
        '  vec3 surfNormal = normalize(vPixelSizeWorldNormal);',
        '  float cosAngle = abs(dot(surfNormal, projDir));',
        '  cosAngle = max(cosAngle, 0.1); // Clamp to avoid extreme values at grazing angles',
        '  float stretchFactor = 1.0 / cosAngle;',
        '',
        '  // Calculate actual pixel dimensions on surface',
        '  float actualPixelSize = max(basePixelWidth, basePixelHeight) * stretchFactor;',
        '',
        '  // Calculate ratio to ideal size',
        '  float pixelRatio = actualPixelSize / uPixelSizeIdealSize;',
        '  pixelSizeMaxRatio = max(pixelSizeMaxRatio, pixelRatio);',
        '}',
        '',
        '// Solid colors: green if under ideal size, red if over',
        'vec3 pixelSizeColor = pixelSizeMaxRatio > 1.0 ? vec3(1.0, 0.0, 0.0) : vec3(0.0, 1.0, 0.0);',
        'float pixelSizeAlpha = pixelSizeMaxRatio > 0.0 ? uPixelSizeWarningOpacity : 0.0;',
        '',
        'if (uPixelSizeOverlayOnly > 0.5) {',
        '  // Overlay pass mode: add warning color to existing material color',
        '  outgoingLight = outgoingLight + pixelSizeColor * pixelSizeAlpha;',
        '} else {',
        '  outgoingLight = mix(outgoingLight, pixelSizeColor, pixelSizeAlpha);',
        '}',
      ].join('\n');

      const withHeader = shader.fragmentShader.replace('#include <common>', fragmentHeader);
      let fragment = withHeader.replace(
        '#include <output_fragment>',
        [pixelSizeBlocks, '#include <output_fragment>'].join('\n')
      );

      if (fragment === withHeader) {
        fragment = withHeader.replace(
          '#include <opaque_fragment>',
          [pixelSizeBlocks, '#include <opaque_fragment>'].join('\n')
        );
      }

      shader.fragmentShader = fragment;

      userData.projectorFrustumPixelSize = {
        applied: true,
        uniforms: shader.uniforms,
      };
    };

    material.needsUpdate = true;
    userData.projectorFrustumPixelSize = { applied: true };
    this.materials.add(material);
  }

  private applyUniforms(count: number) {
    for (const material of this.materials) {
      const data = (material.userData as PixelSizeUserData).projectorFrustumPixelSize;
      if (!data?.uniforms) {
        continue;
      }

      data.uniforms.uPixelSizeProjectorCount.value = count;
      data.uniforms.uPixelSizeWarningColor.value = this.warningColor;
      data.uniforms.uPixelSizeWarningOpacity.value = this.warningOpacity;
      data.uniforms.uPixelSizeIdealSize.value = this.idealPixelSize;
      data.uniforms.uPixelSizeOverlayOnly.value = this.overlayOnly ? 1 : 0;
      data.uniforms.uPixelSizeFacingThreshold.value = this.facingThreshold;

      const target = data.uniforms.uPixelSizeProjectorMatrix.value as THREE.Matrix4[];
      const positionTarget = data.uniforms.uPixelSizeProjectorPosition.value as THREE.Vector3[];
      const directionTarget = data.uniforms.uPixelSizeProjectorDirection.value as THREE.Vector3[];
      const resolutionTarget = data.uniforms.uPixelSizeProjectorResolution.value as THREE.Vector2[];
      const tanHalfFovTarget = data.uniforms.uPixelSizeProjectorTanHalfFov.value as THREE.Vector2[];

      for (let i = 0; i < count; i += 1) {
        target[i].copy(this.matrices[i]);
        positionTarget[i].copy(this.projectorPositions[i]);
        directionTarget[i].copy(this.projectorDirections[i]);
        resolutionTarget[i].copy(this.projectorResolutions[i]);
        tanHalfFovTarget[i].copy(this.projectorTanHalfFov[i]);
      }
    }
  }
}
